/**
 * BatchCutOffModal (S6.1, S6.2) — 封櫃截單 on an open 裝櫃批次.
 *
 * Salvaged gates (server-enforced, mirrored in the UI):
 * - 櫃號 containerNumber + 封條 sealNumber are REQUIRED to seal
 * - per-line qtyLoaded ≤ qtyPlanned (InputNumber max mirrors the 400)
 * - any short-loaded line → sealing raises an owned 訂單異常 (owner 船務,
 *   due window) — surfaced as a live warning before submit
 * - loaded quantities are FIXED at seal; later fixes go through the 改數量
 *   correction loop (S6.2), never a rewrite
 *
 * S6.2 loading capture per Board 5: 供應商出貨箱數/箱號 range, 備註 and 材積
 * CBM per line — manual entry (MVP) with auto-from-product-dims where the
 * product carries dimensions (Excel import spec = open question, 林芊).
 * A LIVE utilization bar tracks Σ CBM against the 櫃型 capacity as you type.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  App,
  Alert,
  Button,
  Col,
  Input,
  InputNumber,
  Modal,
  Progress,
  Row,
  Table,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import { CalculatorOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { extractApiError } from '@/hooks/useSalesOrders';
import { useProductOptions } from '@/hooks/useProducts';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens } from '@/styles/tokens';
import type { ShipmentBatch } from '@/types/api/shipping';
import {
  CONTAINER_CAPACITY_CBM,
  round2,
  toCaptured,
  unitCbmFromDims,
  utilizationPct,
} from '../batchCapture';
import { useBatchCaptureMutations } from '../useBatchCapture';

const { Text } = Typography;

const cutOffSchema = z.object({
  containerNumber: z.string().min(1, 'shipping:batch.validation.containerRequired'),
  sealNumber: z.string().min(1, 'shipping:batch.validation.sealRequired'),
});

type CutOffFormData = z.infer<typeof cutOffSchema>;

const defaultFormValues: CutOffFormData = { containerNumber: '', sealNumber: '' };

interface LoadLine {
  id: string;
  productId: string;
  sku: string;
  factoryName: string;
  qtyPlanned: number;
  qtyLoaded: number;
  cartonCount: number | null;
  cartonFrom: number | null;
  cartonTo: number | null;
  cbm: number;
  note: string | null;
  /** Manual CBM entry stops the auto-from-dims recompute for this line. */
  cbmTouched: boolean;
}

export interface BatchCutOffModalProps {
  open: boolean;
  /** The open batch being sealed (null while closed). */
  batch: ShipmentBatch | null;
  onClose: () => void;
  /** Called after a successful cut-off (page refreshes its list). */
  onDone: () => void;
}

const BatchCutOffModal = ({ open, batch, onClose, onDone }: BatchCutOffModalProps) => {
  const { t } = useTranslation(['shipping', 'common']);
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const formStyles = useFormStyles();

  const { cutOffCapturedBatch } = useBatchCaptureMutations();
  const [loads, setLoads] = useState<LoadLine[]>([]);

  const captured = useMemo(() => (batch ? toCaptured(batch) : null), [batch]);

  // Product dims for the auto-CBM affordance (shared all-rows options fetch).
  const { products } = useProductOptions('all');

  const unitCbmByProductId = useMemo(() => {
    const map = new Map<string, number>();
    products.forEach((product) => {
      const unitCbm = unitCbmFromDims(product);
      if (unitCbm != null) map.set(product.id, unitCbm);
    });
    return map;
  }, [products]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CutOffFormData>({
    resolver: zodResolver(cutOffSchema),
    defaultValues: defaultFormValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  // Loaded quantities default to planned; CBM prefills from dims. Seed ONCE
  // per batch — the dims map tracks the SHARED products-options cache, and a
  // mid-modal revalidation must not wipe operator edits; late dims only
  // refresh the auto-CBM on lines whose CBM the operator hasn't touched.
  const seededBatchIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!captured) {
      seededBatchIdRef.current = null;
      return;
    }
    if (seededBatchIdRef.current === captured.id) {
      setLoads((prev) =>
        prev.map((line) => {
          if (line.cbmTouched) return line;
          const unitCbm = unitCbmByProductId.get(line.productId);
          return unitCbm == null ? line : { ...line, cbm: round2(unitCbm * line.qtyLoaded) };
        })
      );
      return;
    }
    seededBatchIdRef.current = captured.id;
    setLoads(
      captured.lines.map((line) => {
        const unitCbm = unitCbmByProductId.get(line.productId);
        return {
          id: line.id,
          productId: line.productId,
          sku: line.sku,
          factoryName: line.factoryName,
          qtyPlanned: line.qtyPlanned,
          qtyLoaded: line.qtyPlanned,
          cartonCount: line.cartonCount,
          cartonFrom: line.cartonFrom,
          cartonTo: line.cartonTo,
          cbm: line.cbm > 0 ? line.cbm : unitCbm != null ? round2(unitCbm * line.qtyPlanned) : 0,
          note: line.note,
          cbmTouched: line.cbm > 0,
        };
      })
    );
  }, [captured, unitCbmByProductId]);

  const shortCount = loads.filter((line) => line.qtyLoaded < line.qtyPlanned).length;
  const cbmTotal = round2(loads.reduce((sum, line) => sum + line.cbm, 0));
  const containerType = captured?.containerType ?? null;
  const capacity = containerType ? CONTAINER_CAPACITY_CBM[containerType] : null;
  const pct = utilizationPct(cbmTotal, containerType);

  const patchLoad = (id: string, patch: Partial<LoadLine>) =>
    setLoads((prev) =>
      prev.map((candidate) => (candidate.id === id ? { ...candidate, ...patch } : candidate))
    );

  const applyAutoCbm = (line: LoadLine, qtyLoaded = line.qtyLoaded) => {
    const unitCbm = unitCbmByProductId.get(line.productId);
    if (unitCbm == null) return;
    patchLoad(line.id, { cbm: round2(unitCbm * qtyLoaded), cbmTouched: false });
  };

  const closeModal = () => {
    reset(defaultFormValues);
    onClose();
  };

  const onSubmit = async (values: CutOffFormData) => {
    if (!batch) return;
    try {
      await cutOffCapturedBatch(batch.id, {
        containerNumber: values.containerNumber.trim(),
        sealNumber: values.sealNumber.trim(),
        lines: loads.map((line) => ({
          id: line.id,
          qtyLoaded: line.qtyLoaded,
          cbm: line.cbm,
          cartonCount: line.cartonCount,
          cartonFrom: line.cartonFrom,
          cartonTo: line.cartonTo,
          note: line.note?.trim() ? line.note.trim() : null,
        })),
      });
      message.success(t('shipping:batch.messages.cutOff'));
      closeModal();
      onDone();
    } catch (error) {
      const body = extractApiError(error);
      message.error(body.message || t('common:messages.updateFailed'));
    }
  };

  const fieldError = (key: keyof CutOffFormData) =>
    errors[key] ? <Text style={formStyles.errorStyle}>{t(errors[key]?.message ?? '')}</Text> : null;

  return (
    <Modal
      open={open}
      title={t('shipping:batch.cutOffModal.title', { batchNumber: batch?.batchNumber ?? '' })}
      onCancel={closeModal}
      width={900}
      footer={[
        <Button
          key="cancel"
          size="large"
          style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
          onClick={closeModal}
        >
          {t('common:actions.cancel')}
        </Button>,
        <Button
          key="seal"
          type="primary"
          size="large"
          style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
          loading={isSubmitting}
          onClick={() => void handleSubmit(onSubmit)()}
        >
          {t('shipping:batch.cutOffModal.submit')}
        </Button>,
      ]}
    >
      <div style={{ ...formStyles.fieldContainerStyle, marginBottom: '32px', padding: '20px' }}>
        <Text
          type="secondary"
          style={{ display: 'block', fontSize: token.fontSizeSM, marginBottom: '12px' }}
        >
          {t('shipping:batch.cutOffModal.gateHint')}
        </Text>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Text style={formStyles.labelStyle}>
              <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
              {t('shipping:batch.cutOffModal.containerLabel')}
            </Text>
            <Controller
              name="containerNumber"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  size="large"
                  style={formStyles.inputStyle}
                  status={errors.containerNumber ? 'error' : undefined}
                />
              )}
            />
            {fieldError('containerNumber')}
          </Col>
          <Col span={12}>
            <Text style={formStyles.labelStyle}>
              <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
              {t('shipping:batch.cutOffModal.sealLabel')}
            </Text>
            <Controller
              name="sealNumber"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  size="large"
                  style={formStyles.inputStyle}
                  status={errors.sealNumber ? 'error' : undefined}
                />
              )}
            />
            {fieldError('sealNumber')}
          </Col>
        </Row>
      </div>

      <div style={{ ...formStyles.fieldContainerStyle, padding: '20px' }}>
        <Text strong style={formStyles.sectionTitleStyle}>
          {t('shipping:batch.cutOffModal.linesSection')}
        </Text>
        <Text
          type="secondary"
          style={{ display: 'block', fontSize: token.fontSizeSM, marginBottom: '12px' }}
        >
          {t('shipping:batch.cutOffModal.fixedHint')} · {t('shipping:batch.cutOffModal.captureHint')}
        </Text>
        <Table<LoadLine>
          size="small"
          rowKey="id"
          dataSource={loads}
          pagination={false}
          scroll={{ x: 830 }}
          columns={[
            {
              title: t('shipping:notice.modal.sku'),
              dataIndex: 'sku',
              key: 'sku',
              width: 150,
              render: (value: string, line) => (
                <div className="tw-flex tw-flex-col tw-gap-0.5">
                  <span style={{ fontWeight: token.fontWeightStrong }}>{value}</span>
                  <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
                    {line.factoryName}
                  </span>
                </div>
              ),
            },
            {
              title: t('shipping:batch.cutOffModal.qtyPlanned'),
              dataIndex: 'qtyPlanned',
              key: 'qtyPlanned',
              align: 'right',
              width: 90,
              render: (value: number) => (
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
              ),
            },
            {
              title: t('shipping:batch.cutOffModal.qtyLoaded'),
              key: 'qtyLoaded',
              width: 110,
              render: (_: unknown, line) => (
                <InputNumber
                  min={0}
                  max={line.qtyPlanned}
                  value={line.qtyLoaded}
                  size="large"
                  style={{ width: '100%' }}
                  aria-label={t('shipping:batch.cutOffModal.qtyLoadedFor', { sku: line.sku })}
                  onChange={(value) => {
                    const qtyLoaded = value ?? 0;
                    const unitCbm = unitCbmByProductId.get(line.productId);
                    patchLoad(line.id, {
                      qtyLoaded,
                      // Auto CBM follows the load until manually overridden.
                      ...(line.cbmTouched || unitCbm == null
                        ? {}
                        : { cbm: round2(unitCbm * qtyLoaded) }),
                    });
                  }}
                />
              ),
            },
            {
              title: t('shipping:batch.cutOffModal.cartonCount'),
              key: 'cartonCount',
              width: 100,
              render: (_: unknown, line) => (
                <InputNumber
                  min={0}
                  value={line.cartonCount}
                  size="large"
                  style={{ width: '100%' }}
                  aria-label={t('shipping:batch.cutOffModal.cartonCountFor', { sku: line.sku })}
                  onChange={(value) => patchLoad(line.id, { cartonCount: value ?? null })}
                />
              ),
            },
            {
              title: t('shipping:batch.cutOffModal.cartonRange'),
              key: 'cartonRange',
              width: 150,
              render: (_: unknown, line) => (
                <div className="tw-flex tw-items-center tw-gap-1">
                  <InputNumber
                    min={0}
                    value={line.cartonFrom}
                    size="large"
                    style={{ width: '100%' }}
                    aria-label={t('shipping:batch.cutOffModal.cartonFromFor', { sku: line.sku })}
                    onChange={(value) => patchLoad(line.id, { cartonFrom: value ?? null })}
                  />
                  <span style={{ color: token.colorTextSecondary }}>–</span>
                  <InputNumber
                    min={0}
                    value={line.cartonTo}
                    size="large"
                    style={{ width: '100%' }}
                    aria-label={t('shipping:batch.cutOffModal.cartonToFor', { sku: line.sku })}
                    onChange={(value) => patchLoad(line.id, { cartonTo: value ?? null })}
                  />
                </div>
              ),
            },
            {
              title: t('shipping:batch.cutOffModal.cbm'),
              key: 'cbm',
              width: 150,
              render: (_: unknown, line) => {
                const hasDims = unitCbmByProductId.has(line.productId);
                return (
                  <div className="tw-flex tw-items-center tw-gap-1">
                    <InputNumber
                      min={0}
                      step={0.01}
                      value={line.cbm}
                      size="large"
                      style={{ width: '100%' }}
                      aria-label={t('shipping:batch.cutOffModal.cbmFor', { sku: line.sku })}
                      onChange={(value) => patchLoad(line.id, { cbm: value ?? 0, cbmTouched: true })}
                    />
                    {/* auto-from-dims — only affords where the product has dims */}
                    {hasDims && (
                      <Tooltip title={t('shipping:batch.cutOffModal.autoCbm')}>
                        <Button
                          type="text"
                          icon={<CalculatorOutlined style={{ color: token.colorPrimary }} />}
                          aria-label={t('shipping:batch.cutOffModal.autoCbmFor', { sku: line.sku })}
                          onClick={() => applyAutoCbm(line)}
                        />
                      </Tooltip>
                    )}
                  </div>
                );
              },
            },
            {
              title: t('shipping:batch.cutOffModal.note'),
              key: 'note',
              width: 150,
              render: (_: unknown, line) => (
                <Input
                  value={line.note ?? ''}
                  size="large"
                  aria-label={t('shipping:batch.cutOffModal.noteFor', { sku: line.sku })}
                  onChange={(e) => patchLoad(line.id, { note: e.target.value || null })}
                />
              ),
            },
          ]}
        />

        {/* Live utilization — Σ line CBM vs 櫃型 capacity, updates as you type */}
        <div style={{ marginTop: '16px' }}>
          <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            {t('shipping:batch.utilization.label')}
          </Text>
          {capacity != null && pct != null ? (
            <>
              <div className="tw-flex tw-items-center tw-gap-2">
                <Progress
                  percent={Math.min(100, pct)}
                  showInfo={false}
                  size="small"
                  status={pct > 100 ? 'exception' : 'normal'}
                  style={{ flex: 1, margin: 0 }}
                />
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{pct}%</span>
              </div>
              <Text style={{ fontSize: token.fontSizeSM, fontVariantNumeric: 'tabular-nums' }}>
                {t('shipping:batch.utilization.value', {
                  cbm: cbmTotal,
                  capacity,
                  containerType: containerType
                    ? t(`shipping:batch.containerType.${containerType}`)
                    : '',
                })}
              </Text>
              {pct > 100 && (
                <Text
                  style={{
                    display: 'block',
                    fontSize: token.fontSizeSM,
                    color: token.colorError,
                  }}
                >
                  {t('shipping:batch.utilization.over')}
                </Text>
              )}
            </>
          ) : (
            <Text
              style={{
                display: 'block',
                fontSize: token.fontSizeSM,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {t('shipping:batch.utilization.totalOnly', { cbm: cbmTotal })}
            </Text>
          )}
        </div>

        {shortCount > 0 && (
          <Alert
            className="tw-mt-3"
            type="warning"
            showIcon
            message={t('shipping:batch.cutOffModal.shortLoadWarning', { count: shortCount })}
          />
        )}
      </div>
    </Modal>
  );
};

export default BatchCutOffModal;
